/**
 * Generated artwork, for everywhere a photograph has not been supplied.
 *
 * The site launches before the client has photographed their stock, their installs or
 * their staff — and a grid of empty grey rectangles is what makes a finished site look
 * unfinished. These draw something deliberate instead: a recognisable line illustration
 * of the actual device for a product, a branded cover for a project, a monogram for a
 * person.
 *
 * They are **fallbacks, not decoration**. Every one of these components is only reached
 * when the real `imageUrl` is absent, and the moment a photograph is uploaded in the
 * back office it takes over. Nothing here is baked into a record.
 *
 * Drawn as inline SVG rather than shipped as files: they inherit the theme tokens, they
 * cost no request, they are sharp at every size, and they re-colour with the palette if
 * the client changes the accent in Settings.
 */

import { type CSSProperties } from 'react';

/* -------------------------------------------------------------------------- */
/* Shared                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A stream of stable numbers in [0,1) derived from a string.
 *
 * Used to vary the generated covers so two projects side by side do not look identical,
 * while the SAME project always gets the SAME cover — a card whose artwork changed on
 * every render would read as a loading glitch.
 *
 * A stream rather than a single number, which is what this was first. One value used
 * for every property makes them all perfectly correlated: a slug that shifted left also
 * rotated left, by a proportional amount, so the covers really had **one** degree of
 * freedom between them. Three of them in a row on the work index looked like three
 * copies of the same picture, which is exactly the "unfinished" impression the
 * generated artwork exists to avoid. Each draw here is independent.
 */
function seeded(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 15), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h >>> 0) % 100000) / 100000;
  };
}

const frame: CSSProperties = { width: '100%', height: '100%', display: 'block' };

/** The soft ground every device sits on. */
function Ground({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--panel-2)" />
          <stop offset="100%" stopColor="var(--panel-3)" />
        </linearGradient>
        <linearGradient id={`${id}-body`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3B4249" />
          <stop offset="100%" stopColor="#22282D" />
        </linearGradient>
      </defs>
      <rect width="400" height="300" fill={`url(#${id}-bg)`} />
      {/* The contact shadow. Without it the device floats and the drawing reads flat. */}
      <ellipse cx="200" cy="248" rx="120" ry="10" fill="rgba(20,23,26,0.09)" />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Devices                                                                     */
/* -------------------------------------------------------------------------- */

function ReceiptPrinter({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Thermal receipt printer">
      <Ground id={id} />
      {/* The receipt, drawn first so the printer body overlaps its top edge. */}
      <path d="M168 118 h64 v96 q-16 8 -32 0 q-16 -8 -32 0 z" fill="#FAFAF8" stroke="rgba(20,23,26,0.12)" strokeWidth="1.5" />
      <g stroke="rgba(20,23,26,0.22)" strokeWidth="2.5" strokeLinecap="round">
        <path d="M180 134 h40" /><path d="M180 148 h30" /><path d="M180 162 h36" /><path d="M180 176 h22" />
      </g>
      {/* Body */}
      <rect x="128" y="150" width="144" height="86" rx="14" fill={`url(#${id}-body)`} />
      <rect x="128" y="150" width="144" height="30" rx="14" fill="#2C3238" />
      {/* The tear bar and the status light — the two details that say "printer". */}
      <rect x="146" y="146" width="108" height="7" rx="3.5" fill="#1A1F24" />
      <circle cx="248" cy="212" r="5" fill="var(--accent)" />
      <rect x="150" y="205" width="46" height="6" rx="3" fill="rgba(255,255,255,0.14)" />
    </svg>
  );
}

function BarcodeScanner({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Handheld barcode scanner">
      <Ground id={id} />
      {/* Stand */}
      <path d="M150 240 h100 l-14 -22 h-72 z" fill="#2C3238" />
      <rect x="188" y="176" width="14" height="46" fill="#3B4249" />
      {/* Handle, angled, with the head at the top. */}
      <g transform="rotate(-18 200 150)">
        <rect x="180" y="120" width="30" height="82" rx="14" fill={`url(#${id}-body)`} />
        <path d="M172 92 h46 a14 14 0 0 1 14 14 v22 a10 10 0 0 1 -10 10 h-54 a10 10 0 0 1 -10 -10 v-22 a14 14 0 0 1 14 -14 z" fill="#2C3238" />
        <rect x="166" y="118" width="58" height="10" rx="5" fill="#14171A" />
        {/* The beam. The one element that shows what it is for. */}
        <rect x="170" y="121" width="50" height="4" rx="2" fill="var(--accent)" opacity="0.9" />
        <rect x="186" y="146" width="18" height="7" rx="3.5" fill="rgba(255,255,255,0.16)" />
      </g>
    </svg>
  );
}

function PosTerminalArt({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Point-of-sale terminal">
      <Ground id={id} />
      {/* Base and neck */}
      <path d="M150 244 h100 l-12 -18 h-76 z" fill="#2C3238" />
      <rect x="192" y="176" width="16" height="54" fill="#3B4249" />
      {/* Main screen */}
      <rect x="112" y="70" width="176" height="114" rx="12" fill={`url(#${id}-body)`} />
      <rect x="122" y="80" width="156" height="94" rx="7" fill="#0D1211" />
      {/* A ticket and a pay key, so the screen reads as a till rather than a monitor. */}
      <g fill="rgba(255,255,255,0.16)">
        <rect x="132" y="92" width="70" height="7" rx="3.5" />
        <rect x="132" y="106" width="56" height="7" rx="3.5" />
        <rect x="132" y="120" width="64" height="7" rx="3.5" />
        <rect x="132" y="134" width="44" height="7" rx="3.5" />
      </g>
      <rect x="214" y="92" width="54" height="34" rx="8" fill="rgba(255,255,255,0.09)" />
      <rect x="214" y="132" width="54" height="30" rx="8" fill="var(--accent)" />
      <rect x="132" y="152" width="86" height="10" rx="5" fill="rgba(3,190,155,0.28)" />
    </svg>
  );
}

function CashDrawer({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Steel cash drawer">
      <Ground id={id} />
      {/* Case */}
      <rect x="96" y="130" width="208" height="104" rx="10" fill={`url(#${id}-body)`} />
      {/* The open drawer front, pulled forward. */}
      <rect x="106" y="168" width="188" height="62" rx="8" fill="#2C3238" />
      <rect x="106" y="168" width="188" height="10" rx="5" fill="#464E55" />
      {/* Note bays and coin cups — what makes it a cash drawer and not a box. */}
      <g fill="rgba(255,255,255,0.10)">
        <rect x="120" y="188" width="36" height="30" rx="4" />
        <rect x="162" y="188" width="36" height="30" rx="4" />
        <rect x="204" y="188" width="36" height="30" rx="4" />
        <rect x="246" y="188" width="36" height="30" rx="4" />
      </g>
      <rect x="184" y="146" width="32" height="7" rx="3.5" fill="rgba(255,255,255,0.16)" />
      <circle cx="290" cy="150" r="5" fill="var(--accent)" />
    </svg>
  );
}

function PaperRoll({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Thermal paper rolls">
      <Ground id={id} />
      {/* Two rolls, one behind, so the drawing has depth at card size. */}
      <g>
        <ellipse cx="150" cy="140" rx="46" ry="46" fill="#E7EAE8" stroke="rgba(20,23,26,0.12)" strokeWidth="1.5" />
        <ellipse cx="150" cy="140" rx="15" ry="15" fill="var(--panel-3)" stroke="rgba(20,23,26,0.14)" strokeWidth="1.5" />
      </g>
      <g>
        <ellipse cx="238" cy="168" rx="56" ry="56" fill="#FAFAF8" stroke="rgba(20,23,26,0.12)" strokeWidth="1.5" />
        <ellipse cx="238" cy="168" rx="18" ry="18" fill="var(--panel-3)" stroke="rgba(20,23,26,0.14)" strokeWidth="1.5" />
        {/* The loose tail, which is what identifies it as a roll rather than a disc. */}
        <path d="M294 168 q22 4 26 26 q2 18 -14 24" fill="none" stroke="rgba(20,23,26,0.2)" strokeWidth="3" strokeLinecap="round" />
      </g>
      <rect x="196" y="238" width="84" height="6" rx="3" fill="rgba(3,190,155,0.35)" />
    </svg>
  );
}

function GenericDevice({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 400 300" style={frame} role="img" aria-label="Hardware">
      <Ground id={id} />
      <rect x="118" y="112" width="164" height="112" rx="14" fill={`url(#${id}-body)`} />
      <rect x="132" y="126" width="136" height="66" rx="8" fill="#0D1211" />
      <rect x="146" y="150" width="52" height="8" rx="4" fill="rgba(255,255,255,0.16)" />
      <rect x="146" y="166" width="34" height="8" rx="4" fill="rgba(3,190,155,0.4)" />
      <circle cx="256" cy="208" r="5" fill="var(--accent)" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Public components                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Pick the illustration from the product's category, then its name.
 *
 * The category is the reliable signal and is checked first. The name is a fallback for
 * a product filed under nothing, which the back office allows.
 */
export function ProductArtwork({
  category,
  name,
  sku,
}: {
  /**
   * The product's category as free text — a slug (`receipt-printers`) or a display
   * name (`Receipt printers`). Both are only ever lowercased and matched against the
   * patterns below, and the two halves of the app hold different ones: the storefront
   * has the slug, the back office the name. Typing this as "the slug" and then passing
   * a name at one call site would be a comment that lies.
   *
   * It matters more than it looks. "Thermal paper roll 80 mm" matches `/thermal/` on
   * its name alone and would be drawn as a printer; its category is what makes it a
   * paper roll.
   */
  category?: string | null;
  name?: string | null;
  sku?: string | null;
}) {
  const id = `pa-${(sku ?? name ?? 'x').replace(/[^a-zA-Z0-9]/g, '')}`;
  const haystack = `${category ?? ''} ${name ?? ''}`.toLowerCase();

  if (/receipt|printer|thermal|label/.test(haystack)) return <ReceiptPrinter id={id} />;
  if (/scanner|barcode|imager/.test(haystack)) return <BarcodeScanner id={id} />;
  if (/terminal|pos|register|display/.test(haystack)) return <PosTerminalArt id={id} />;
  if (/drawer|cash/.test(haystack)) return <CashDrawer id={id} />;
  if (/paper|roll|accessor|cable|consumable/.test(haystack)) return <PaperRoll id={id} />;
  return <GenericDevice id={id} />;
}

/**
 * A branded cover for a project or an article that has no photograph.
 *
 * Geometry seeded from the slug, so every card differs and each one is stable. Built
 * from the same angled parallelograms as the company's logo mark, so it reads as the
 * brand's own furniture rather than as a stock pattern.
 */
export function CoverArtwork({ seed, label }: { seed: string; label?: string }) {
  const id = `cv-${seed.replace(/[^a-zA-Z0-9]/g, '')}`;
  const rand = seeded(seed);

  // Six independent draws. The ranges are wide enough to read as different pictures at
  // card size — where the whole cover is about 300px across — and bounded so the mark's
  // bars always still cross the frame rather than drifting out of it.
  const shiftX = Math.round(rand() * 130) - 65;
  const shiftY = Math.round(rand() * 46) - 23;
  const rotate = Math.round(rand() * 28) - 14;
  const scale = (0.82 + rand() * 0.56).toFixed(2);
  const gradientAngle = Math.round(rand() * 150) - 30;
  // Which bar carries the brightest fill. Rotating the opacity ramp changes where the
  // eye lands, which alters a cover more than moving the whole group does.
  const phase = Math.floor(rand() * 6);

  const RAMP = [0.95, 0.5, 0.7, 0.35, 0.22, 0.5];
  /** The six bars of the mark, in draw order. */
  const BARS = [
    'M96 40 l58 0 l-38 62 l-58 0 z',
    'M176 40 l58 0 l-38 62 l-58 0 z',
    'M136 116 l58 0 l-38 62 l-58 0 z',
    'M216 116 l58 0 l-38 62 l-58 0 z',
    'M256 40 l58 0 l-38 62 l-58 0 z',
    'M296 116 l58 0 l-38 62 l-58 0 z',
  ];

  return (
    <svg
      viewBox="0 0 400 250"
      /**
       * `slice`, not the default `meet`. This cover is used at three different aspect
       * ratios — 16:10 on an index card, 4:3 beside a case-study summary, 16:6 as a
       * banner on the project page — and `meet` letterboxes at any of them that is not
       * 16:10. On the project page that showed as the artwork floating in the middle of
       * the panel with a white gutter down each side, which reads as a broken image.
       * The composition is a cropped pattern by design, so filling and cropping is both
       * correct and what it was drawn for.
       */
      preserveAspectRatio="xMidYMid slice"
      style={frame}
      role="img"
      aria-label={label ?? 'Cover'}
    >
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#EAF4F1" />
          <stop offset="55%" stopColor="#E0EFEA" />
          <stop offset="100%" stopColor="#F2F8F6" />
        </linearGradient>
        <linearGradient id={`${id}-a`} x1="0" y1="0" x2="1" y2="1" gradientTransform={`rotate(${gradientAngle} 0.5 0.5)`}>
          <stop offset="0%" stopColor="#03BE9B" />
          <stop offset="100%" stopColor="#017C66" />
        </linearGradient>
        <clipPath id={`${id}-clip`}><rect width="400" height="250" /></clipPath>
      </defs>

      <g clipPath={`url(#${id}-clip)`}>
        <rect width="400" height="250" fill={`url(#${id}-g)`} />
        <g
          // Scaled about the centre, so a larger cover grows outwards and crops rather
          // than sliding towards the corner.
          transform={`translate(${shiftX} ${shiftY}) rotate(${rotate} 200 125) translate(200 125) scale(${scale}) translate(-200 -125)`}
          opacity="0.92"
        >
          {/* The mark's slanted bars, enlarged and cropped by the frame. */}
          {BARS.map((d, i) => (
            <path key={d} d={d} fill={`url(#${id}-a)`} opacity={RAMP[(i + phase) % RAMP.length]} />
          ))}
        </g>
      </g>
    </svg>
  );
}

/**
 * A person with no photograph: their initials, on the brand's wash.
 *
 * Initials rather than a silhouette icon — a generic avatar outline says "no data",
 * whereas initials say "this is a specific person we have not photographed yet".
 */
export function PersonArtwork({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(140deg, rgba(3,190,155,0.16), rgba(1,124,102,0.08))',
      }}
      role="img"
      aria-label={name}
    >
      <span style={{ fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: 700, letterSpacing: '-0.03em', color: 'var(--accent-deep)' }}>
        {initials}
      </span>
    </div>
  );
}
